import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  type Placement,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import * as React from "react";

import { cn } from "@/shadcn/lib/utils";

export type TooltipProps = {
  /** The element that will trigger the tooltip to appear */
  readonly trigger: React.ReactNode;
  /** The content to display in the tooltip */
  readonly content: React.ReactNode;
  /** Where the tooltip should be placed relative to the trigger */
  readonly placement?: Placement;
  readonly className?: string;
};

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  ({ content, trigger, placement = "top", className }, forwardedRef) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const {
      refs: { setReference, setFloating },
      floatingStyles,
      placement: resolvedPlacement,
      context,
    } = useFloating({
      placement,
      onOpenChange: setIsOpen,
      whileElementsMounted: autoUpdate,
      middleware: [offset(5), flip({ padding: 8 }), shift({ padding: 8 })],
    });

    const hover = useHover(context, { move: false });
    const focus = useFocus(context);
    const role = useRole(context, { role: "tooltip" });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, role]);

    const basePointerStyles = "absolute border-white";
    const topStyles = "border-x-[6px] border-t-[6px] border-x-transparent -bottom-1.5";
    const bottomStyles = "-top-1.5 border-x-[6px] border-b-[6px] border-x-transparent";
    const leftStyles = "-right-1.5 border-y-[6px] border-l-[6px] border-y-transparent";
    const rightStyles = "-left-1.5 border-y-[6px] border-r-[6px] border-y-transparent";

    const styleMap: Record<Placement, string> = {
      "top": cn(topStyles, "left-1/2 -translate-x-1/2"),
      "top-start": cn(topStyles, "left-1"),
      "top-end": cn(topStyles, "right-1"),
      "bottom": cn(bottomStyles, "left-1/2 -translate-x-1/2"),
      "bottom-start": cn(bottomStyles, "left-1"),
      "bottom-end": cn(bottomStyles, "right-1"),
      "left": cn(leftStyles, "top-1/2 -translate-y-1/2"),
      "left-start": cn(leftStyles, "top-1"),
      "left-end": cn(leftStyles, "bottom-1"),
      "right": cn(rightStyles, "top-1/2 -translate-y-1/2"),
      "right-start": cn(rightStyles, "top-1"),
      "right-end": cn(rightStyles, "bottom-1"),
    };

    const pointerStyle = cn(basePointerStyles, styleMap[resolvedPlacement]);

    if (!content) {
      return trigger as React.ReactElement;
    }

    return (
      <>
        <div
          className="flex cursor-pointer items-center justify-center"
          ref={(node) => {
            setReference(node);
            if (typeof forwardedRef === "function") forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
          }}
          role="button"
          tabIndex={0}
          {...getReferenceProps()}
        >
          {trigger}
        </div>

        <FloatingPortal>
          {isOpen && (
            <div
              className={cn("absolute z-120 w-max", className)}
              ref={setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <div className="bg-white p-3 text-center text-body2 shadow-tooltip select-none">
                {content}
              </div>
              <div className={pointerStyle} />
            </div>
          )}
        </FloatingPortal>
      </>
    );
  },
);

Tooltip.displayName = "Tooltip";
